import { ExpandMore as ExpandMoreIcon } from 'components/icons';
import PermissionButton from 'components/buttons/PermissionButton';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import InfoTooltip from 'components/InfoTooltip';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_HEAD_CLASS, SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Control, Path, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useGetInvoiceTotalsQuery, useUpdateInvoiceRoundingAdjustmentsMutation } from 'store/api/invoices';
import { FeatureFlag } from 'types/features';
import {
    InvoiceData,
    InvoiceTotals,
    InvoiceType,
    RoundingAdjustmentsDto,
    UpdateRoundingAdjustmentsDto,
} from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { SECTION_CARD, SECTION_HEADER, SECTION_PANEL, SECTION_TITLE } from '../details-tab/SectionCard';

/** The matrix is a small table: 8px cells, the row's first and last 16px from the edge. */
const HEAD_CELL = cn(SIMPLE_TABLE_HEAD_CLASS, 'p-2 first:pl-4 last:pr-4');
const CELL = cn(SIMPLE_TABLE_CELL_CLASS, 'p-2');
const ROW_LABEL = 'text-body2';

interface Props {
    invoice: InvoiceData;
    isInViewMode: boolean;
    invoiceTotals?: InvoiceTotals;
}

interface RoundingAdjustmentsFormFields {
    vat: string;
    totalWithVat: string;
    totalWithoutVat: string;
}

interface RoundingMatrixFormData {
    invoiceCurrency: RoundingAdjustmentsFormFields;
    paymentCurrency: RoundingAdjustmentsFormFields;
    baseCurrency: RoundingAdjustmentsFormFields;
    isDeltaVatRoundingEnabled: boolean;
}

const toFormFields = (adjustments?: RoundingAdjustmentsDto | null): RoundingAdjustmentsFormFields => ({
    vat: adjustments?.vat !== null && adjustments?.vat !== undefined ? String(adjustments.vat) : '0.00',
    totalWithVat:
        adjustments?.totalWithVat !== null && adjustments?.totalWithVat !== undefined
            ? String(adjustments.totalWithVat)
            : '0.00',
    totalWithoutVat:
        adjustments?.totalWithoutVat !== null && adjustments?.totalWithoutVat !== undefined
            ? String(adjustments.totalWithoutVat)
            : '0.00',
});

const parseFieldValue = (value: string): number | null => {
    const normalized = value.replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed;
};

const toDto = (fields: RoundingAdjustmentsFormFields): RoundingAdjustmentsDto => ({
    vat: parseFieldValue(fields.vat),
    totalWithVat: parseFieldValue(fields.totalWithVat),
    totalWithoutVat: parseFieldValue(fields.totalWithoutVat),
});

interface RoundingInputProps {
    control: Control<RoundingMatrixFormData>;
    fieldName: Path<RoundingMatrixFormData>;
    disabled?: boolean;
    isInViewMode: boolean;
}

const RoundingInput = ({ control, fieldName, disabled = false, isInViewMode }: RoundingInputProps) => (
    <DecimalInput control={control} name={fieldName} allowNegative disabled={disabled || isInViewMode} />
);

function useRoundingMatrixForm(invoice: InvoiceData, invoiceTotals?: InvoiceTotals) {
    const [updateRoundingAdjustments, { isLoading }] = useUpdateInvoiceRoundingAdjustmentsMutation();

    const { control, handleSubmit } = useForm<RoundingMatrixFormData>({
        values: {
            invoiceCurrency: toFormFields(invoiceTotals?.invoiceCurrency.roundingAdjustments),
            paymentCurrency: toFormFields(invoiceTotals?.paymentCurrency.roundingAdjustments),
            baseCurrency: toFormFields(invoiceTotals?.baseCurrency.roundingAdjustments),
            isDeltaVatRoundingEnabled:
                invoice.isDeltaVatRoundingFeasible === false ? false : invoice.isDeltaVatRoundingEnabled,
        },
    });

    const save = (onSaved?: () => void) => {
        handleSubmit(async (data) => {
            const dto: UpdateRoundingAdjustmentsDto = {
                invoiceCurrency: toDto(data.invoiceCurrency),
                paymentCurrency: toDto(data.paymentCurrency),
                baseCurrency: toDto(data.baseCurrency),
                isDeltaVatRoundingEnabled: data.isDeltaVatRoundingEnabled,
            };
            await updateRoundingAdjustments({ invoiceId: invoice.id, ...dto }).unwrap();
            onSaved?.();
        })();
    };

    return { control, save, isLoading };
}

const ROUNDING_ROWS = [
    { labelId: 'invoices.totals-tab.rounding-matrix.net', field: 'totalWithoutVat' },
    { labelId: 'invoices.totals-tab.rounding-matrix.vat', field: 'vat' },
    { labelId: 'invoices.totals-tab.rounding-matrix.gross', field: 'totalWithVat' },
] as const;
const ROUNDING_CURRENCIES = [
    { key: 'baseCurrency', labelId: 'invoices.totals-tab.rounding-matrix.base-currency', name: 'base' },
    { key: 'invoiceCurrency', labelId: 'invoices.totals-tab.rounding-matrix.invoice-currency', name: 'invoice' },
    { key: 'paymentCurrency', labelId: 'invoices.totals-tab.rounding-matrix.payment-currency', name: 'payment' },
] as const;

/** The new design's matrix: the kit Table, each cell an input named by its total and currency, the figure at the end. */
function RoundingMatrixTable({
    control,
    invoiceTotals,
    isInViewMode,
}: Omit<Props, 'invoice'> & { control: Control<RoundingMatrixFormData> }) {
    return (
        <Table>
            <TableCaption className="sr-only">
                <FormattedMessage id="invoices.totals-tab.rounding-matrix.caption" />
            </TableCaption>
            <TableHeader>
                <TableRow>
                    <td className="h-10 p-2 ps-0" />
                    {ROUNDING_CURRENCIES.map((currency) => (
                        <TableHead key={currency.key} scope="col" className="w-[27%] text-end">
                            <FormattedMessage id={currency.labelId} />
                        </TableHead>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBody>
                {ROUNDING_ROWS.map((row) => (
                    <TableRow key={row.field}>
                        <TableHead scope="row" className="ps-0 font-normal">
                            <FormattedMessage id={row.labelId} />
                        </TableHead>
                        {ROUNDING_CURRENCIES.map((currency) => (
                            <TableCell
                                key={currency.key}
                                className="[&_input]:text-end [&_input]:text-sm [&_input]:tabular-nums"
                            >
                                <DecimalInput
                                    control={control}
                                    name={`${currency.key}.${row.field}`}
                                    allowNegative
                                    disabled={
                                        isInViewMode || (invoiceTotals?.[currency.key].invoice[row.field] ?? 0) === 0
                                    }
                                    inputProps={{
                                        'aria-label': t('invoices.totals-tab.rounding-matrix.cell', {
                                            row: t(row.labelId),
                                            currency: currency.name,
                                        }),
                                    }}
                                />
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

/** The adjustment per total and currency, a cell off while its total is 0; a correction's delta VAT rounding above. */
function RoundingMatrixFields({
    control,
    invoice,
    invoiceTotals,
    isInViewMode,
    isNew = false,
}: Props & { control: Control<RoundingMatrixFormData>; isNew?: boolean }) {
    const isCorrectionAutoVatRoundingEnabled = useIsFeatureEnabled(FeatureFlag.CORRECTION_AUTO_VAT_ROUNDING);
    const isCorrectionInvoice = invoice.invoiceTypeCode === InvoiceType.CORRECTION;

    const isBaseNetZero = (invoiceTotals?.baseCurrency.invoice.totalWithoutVat ?? 0) === 0;
    const isInvoiceNetZero = (invoiceTotals?.invoiceCurrency.invoice.totalWithoutVat ?? 0) === 0;
    const isPaymentNetZero = (invoiceTotals?.paymentCurrency.invoice.totalWithoutVat ?? 0) === 0;

    const isBaseVatZero = (invoiceTotals?.baseCurrency.invoice.vat ?? 0) === 0;
    const isInvoiceVatZero = (invoiceTotals?.invoiceCurrency.invoice.vat ?? 0) === 0;
    const isPaymentVatZero = (invoiceTotals?.paymentCurrency.invoice.vat ?? 0) === 0;

    const isBaseGrossZero = (invoiceTotals?.baseCurrency.invoice.totalWithVat ?? 0) === 0;
    const isInvoiceGrossZero = (invoiceTotals?.invoiceCurrency.invoice.totalWithVat ?? 0) === 0;
    const isPaymentGrossZero = (invoiceTotals?.paymentCurrency.invoice.totalWithVat ?? 0) === 0;

    return (
        <>
            {isCorrectionAutoVatRoundingEnabled && isCorrectionInvoice && (
                <div className="mb-2">
                    <CheckboxInput
                        control={control}
                        name="isDeltaVatRoundingEnabled"
                        label="invoices.totals-tab.rounding-matrix.delta-vat-rounding"
                        disabled={isInViewMode || invoice.isDeltaVatRoundingFeasible === false}
                        tooltip={
                            invoice.isDeltaVatRoundingFeasible === false ? (
                                <InfoTooltip text="invoices.totals-tab.rounding-matrix.delta-vat-rounding-not-feasible" />
                            ) : undefined
                        }
                    />
                </div>
            )}
            {isNew ? (
                <RoundingMatrixTable control={control} invoiceTotals={invoiceTotals} isInViewMode={isInViewMode} />
            ) : (
                <table className="w-full text-left text-sm">
                    <TableHeader className="[&_tr]:border-b-0">
                        <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                            <TableHead className={HEAD_CELL} />
                            <TableHead className={HEAD_CELL}>
                                <p className="text-body2 font-semibold">
                                    <FormattedMessage id="invoices.totals-tab.rounding-matrix.base-currency" />
                                </p>
                            </TableHead>
                            <TableHead className={HEAD_CELL}>
                                <p className="text-body2 font-semibold">
                                    <FormattedMessage id="invoices.totals-tab.rounding-matrix.invoice-currency" />
                                </p>
                            </TableHead>
                            <TableHead className={HEAD_CELL}>
                                <p className="text-body2 font-semibold">
                                    <FormattedMessage id="invoices.totals-tab.rounding-matrix.payment-currency" />
                                </p>
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                            <TableCell className={CELL}>
                                <p className={ROW_LABEL}>
                                    <FormattedMessage id="invoices.totals-tab.rounding-matrix.net" />
                                </p>
                            </TableCell>
                            <TableCell className={CELL}>
                                <RoundingInput
                                    control={control}
                                    fieldName="baseCurrency.totalWithoutVat"
                                    disabled={isBaseNetZero}
                                    isInViewMode={isInViewMode}
                                />
                            </TableCell>
                            <TableCell className={CELL}>
                                <RoundingInput
                                    control={control}
                                    fieldName="invoiceCurrency.totalWithoutVat"
                                    disabled={isInvoiceNetZero}
                                    isInViewMode={isInViewMode}
                                />
                            </TableCell>
                            <TableCell className={CELL}>
                                <RoundingInput
                                    control={control}
                                    fieldName="paymentCurrency.totalWithoutVat"
                                    disabled={isPaymentNetZero}
                                    isInViewMode={isInViewMode}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                            <TableCell className={CELL}>
                                <p className={ROW_LABEL}>
                                    <FormattedMessage id="invoices.totals-tab.rounding-matrix.vat" />
                                </p>
                            </TableCell>
                            <TableCell className={CELL}>
                                <RoundingInput
                                    control={control}
                                    fieldName="baseCurrency.vat"
                                    disabled={isBaseVatZero}
                                    isInViewMode={isInViewMode}
                                />
                            </TableCell>
                            <TableCell className={CELL}>
                                <RoundingInput
                                    control={control}
                                    fieldName="invoiceCurrency.vat"
                                    disabled={isInvoiceVatZero}
                                    isInViewMode={isInViewMode}
                                />
                            </TableCell>
                            <TableCell className={CELL}>
                                <RoundingInput
                                    control={control}
                                    fieldName="paymentCurrency.vat"
                                    disabled={isPaymentVatZero}
                                    isInViewMode={isInViewMode}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                            <TableCell className={CELL}>
                                <p className={ROW_LABEL}>
                                    <FormattedMessage id="invoices.totals-tab.rounding-matrix.gross" />
                                </p>
                            </TableCell>
                            <TableCell className={CELL}>
                                <RoundingInput
                                    control={control}
                                    fieldName="baseCurrency.totalWithVat"
                                    disabled={isBaseGrossZero}
                                    isInViewMode={isInViewMode}
                                />
                            </TableCell>
                            <TableCell className={CELL}>
                                <RoundingInput
                                    control={control}
                                    fieldName="invoiceCurrency.totalWithVat"
                                    disabled={isInvoiceGrossZero}
                                    isInViewMode={isInViewMode}
                                />
                            </TableCell>
                            <TableCell className={CELL}>
                                <RoundingInput
                                    control={control}
                                    fieldName="paymentCurrency.totalWithVat"
                                    disabled={isPaymentGrossZero}
                                    isInViewMode={isInViewMode}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </table>
            )}
        </>
    );
}

const RoundingMatrixAccordion = ({ invoice, invoiceTotals, isInViewMode }: Props) => {
    const { control, save, isLoading } = useRoundingMatrixForm(invoice, invoiceTotals);

    return (
        <Collapsible className={SECTION_CARD}>
            <CollapsibleTrigger
                className={cn(
                    SECTION_HEADER,
                    'group focus-visible:ring-ring/50 w-full cursor-pointer rounded-md text-left outline-none focus-visible:ring-3'
                )}
            >
                <span className={cn(SECTION_TITLE, 'my-2.5 ml-2 flex-1')}>
                    <FormattedMessage id="invoices.totals-tab.rounding-matrix.title" />
                </span>
                <ExpandMoreIcon className="text-grey-500 transition-transform group-data-[panel-open]:rotate-90" />
            </CollapsibleTrigger>
            <CollapsibleContent keepMounted className={SECTION_PANEL}>
                <div className="border-divider border-t p-4">
                    <RoundingMatrixFields
                        control={control}
                        invoice={invoice}
                        invoiceTotals={invoiceTotals}
                        isInViewMode={isInViewMode}
                    />
                    <div className="mt-2 flex justify-end">
                        <PermissionButton
                            permissions={['UPDATE.INVOICES.ROUNDING_MATRIX']}
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() => save()}
                            disabled={isLoading || isInViewMode}
                        >
                            <FormattedMessage id="save" />
                        </PermissionButton>
                    </div>
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
};

/** The same matrix in a dialog, as the new design's Summary opens it; Save closes it once the adjustments are saved. */
export function RoundingMatrixDialog({
    invoice,
    isInViewMode,
    onClose,
}: Omit<Props, 'invoiceTotals'> & { onClose: () => void }) {
    const { data: invoiceTotals } = useGetInvoiceTotalsQuery(invoice.id);
    const { control, save, isLoading } = useRoundingMatrixForm(invoice, invoiceTotals);

    return (
        <>
            <ModalHeader title={t('invoices.totals-tab.rounding-matrix.title')} onCancel={onClose} />
            <div className="overflow-y-auto px-6 py-3 max-sm:px-4">
                <RoundingMatrixFields
                    control={control}
                    invoice={invoice}
                    invoiceTotals={invoiceTotals}
                    isInViewMode={isInViewMode}
                    isNew
                />
            </div>
            <ModalFooter
                onCancel={onClose}
                onSave={() => save(onClose)}
                savePermissions={['UPDATE.INVOICES.ROUNDING_MATRIX']}
                disableSave={isLoading}
                readOnly={isInViewMode}
            />
        </>
    );
}

export default RoundingMatrixAccordion;
